import {
  flattenObjectWithPath,
  areValuesSemanticallySame,
} from './validationUtils';



const normalizeQuestions = (questions) => {
  if (!questions) return {};
  if (Array.isArray(questions)) {
    return questions.reduce((acc, question) => {
      if (!question?.id) return acc;
      acc[question.id] =
        question.answer !== undefined
          ? question.answer
          : question.value !== undefined
          ? question.value
          : '';
      return acc;
    }, {});
  }
  return questions || {};
};

const hasNoCustomData = (customData) => {
  if (!customData) return true;
  if (Array.isArray(customData)) {
    return customData.length === 0;
  }
  return Object.keys(customData).length === 0;
};

const getGoalCustomSectionIds = (allGoalData) => {
  return (
    allGoalData?.DataEntrySelection?.CustomDataEntrySections ||
    allGoalData?.dataCollection?.DataEntrySelection?.CustomDataEntrySections ||
    allGoalData?.dataCollection?.DataCollection?.DataEntrySelection
      ?.CustomDataEntrySections ||
    allGoalData?.DataCollection?.DataEntrySelection?.CustomDataEntrySections ||
    []
  );
};

const hasSectionResponses = (questions) => {
  if (!questions) return false;
  if (Array.isArray(questions)) {
    return questions.some((question) => {
      const answer =
        question?.answer !== undefined
          ? question.answer
          : question?.value !== undefined
          ? question.value
          : '';
      if (Array.isArray(answer)) return answer.length > 0;
      if (answer && typeof answer === 'object') return true;
      return answer !== null && answer !== undefined && answer !== '';
    });
  }
  if (typeof questions === 'object') {
    return Object.values(questions).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object') {
        if ('answer' in value && value.answer !== undefined) {
          const answer = value.answer;
          if (Array.isArray(answer)) return answer.length > 0;
          return answer !== null && answer !== '';
        }
        if ('value' in value && value.value !== undefined) {
          const v = value.value;
          if (Array.isArray(v)) return v.length > 0;
          return v !== null && v !== '';
        }
        return true;
      }
      return value !== null && value !== undefined && value !== '';
    });
  }
  return false;
};

const shouldKeepSection = (sectionId, questions, shouldFilterByIds, ids) => {
  if (!sectionId) return false;
  if (!shouldFilterByIds) return true;
  if (ids.includes(sectionId)) return true;
  return hasSectionResponses(questions);
};

const convertCustomDataToCustomSection = (customData, allGoalData) => {
  if (hasNoCustomData(customData)) {
    return [];
  }

  const customSectionIds = getGoalCustomSectionIds(allGoalData);
  const shouldFilterByIds =
    Array.isArray(customSectionIds) && customSectionIds.length > 0;

  if (Array.isArray(customData)) {
    return customData
      .filter((section) => {
        if (!section?.docId) return false;
        return shouldKeepSection(
          section.docId,
          section.questions,
          shouldFilterByIds,
          customSectionIds
        );
      })
      .map((section) => ({
        docId: section.docId,
        title: section.title,
        definition: section.definition,
        questions: normalizeQuestions(section.questions),
      }));
  }

  // Check if this is the old flat format (keys contain underscores)
  const hasOldFormat = Object.keys(customData).some((key) => key.includes('_'));
  if (hasOldFormat) {
    // Handle old flat format: sectionId_questionId
    const sectionMap = new Map();
    Object.entries(customData).forEach(([key, value]) => {
      const [sectionId, questionId] = key.split('_');
      if (!sectionMap.has(sectionId)) {
        sectionMap.set(sectionId, {
          docId: sectionId,
          questions: {},
        });
      }
      sectionMap.get(sectionId).questions[questionId] = value;
    });

    return Array.from(sectionMap.values()).filter((section) =>
      shouldKeepSection(
        section.docId,
        section.questions,
        shouldFilterByIds,
        customSectionIds
      )
    );
  }

  // Handle new nested object/array format
  const customSections = [];

  Object.entries(customData).forEach(([sectionId, questions]) => {
    // Only include sections that are part of this goal
    if (
      shouldKeepSection(
        sectionId,
        questions,
        shouldFilterByIds,
        customSectionIds
      )
    ) {
      customSections.push({
        docId: sectionId,
        questions: normalizeQuestions(questions),
      });
    }
  });
  return customSections;
};

// Get prompting structure based on type
export const getPromptingStructure = (type) => {
  if (type === 'P+ & P-') {
    return {
      pMinus: '',
      pPlus: '',
    };
  } else if (
    type === 'IVP, DVP, PVP, GP, PP' ||
    type === 'I, VP (IVP, DVP, PVP), GP, PP, ViP, M'
  ) {
    return {
      I: 0,
      VP: 0,
      IVP: 0,
      DVP: 0,
      PVP: 0,
      GP: 0,
      PP: 0,
      ViP: 0,
      M: 0,
    };
  }
  return {};
};

// Transform raw data points to form state
export const transformRawDataToFormState = (
  rawDataPoints,
  criteria = [],
  subGoalIndex = null,
  promptingType = null,
  allGoalData = null
) => {
  const result = rawDataPoints.map((dataPoint, idx) => {
    let criteriaData = [];
    if (dataPoint.criteria && dataPoint.criteria.length > 0) {
      criteriaData = dataPoint.criteria.map((criterion) => ({
        MeasurementType: criterion.MeasurementType || criterion.measurementType,
        Target: criterion.Target || criterion.target,
        TargetDuration: criterion.TargetDuration || criterion.measurement,
        Variable: criterion.Variable || criterion.variable,
        value: criterion.value || 0,
      }));
    } else if (criteria && criteria.length > 0) {
      criteriaData = criteria.map((item) => ({
        MeasurementType: item.MeasurementType,
        Target: item.Target,
        TargetDuration: item.TargetDuration,
        Variable: item.Variable,
        value: 0,
      }));
    }

    let promptingData = {};
    if (dataPoint.prompting) {
      promptingData = dataPoint.prompting;
    } else {
      promptingData = getPromptingStructure(promptingType);
    }

    // Debug currentGoal logic
    const computedCurrentGoal =
      typeof dataPoint.currentGoal !== 'undefined'
        ? dataPoint.currentGoal
        : subGoalIndex
        ? subGoalIndex
        : 'main';

    // For prompting goals, use prompting_measurement if available, otherwise use goal_value
    // For other goals, use goal_value
    const value = dataPoint.prompting_measurement !== undefined && dataPoint.prompting_measurement !== '' 
      ? dataPoint.prompting_measurement 
      : (dataPoint.goal_value ?? 'N/A');

    return {
      id: dataPoint.id,
      value: value,
      trialsNumber: dataPoint.trialsNumber || [0],
      trailsValue: dataPoint.trials_value || {
        TrailsMin: '',
        TrailsMax: '',
      },
      selectedOptions: [
        ...(dataPoint.abc_data ? ['Add ABC Data'] : []),
        ...(dataPoint.phase_change ? ['Add Phase Change Line'] : []),
        ...(dataPoint.preceding_behavior ? ['Add Preceding Behavior'] : []),
        ...(dataPoint.event ? ['Add Event Line'] : []),
        ...(dataPoint.timeOfCollection || dataPoint.timeOfCollectionSpecific
          ? ['Add Time of Collection']
          : []),
        ...(dataPoint.promptingLevel ? ['Add Prompting Level'] : []),
        ...(dataPoint.sessionSummary ? ['Add Session Summary'] : []),
        ...(dataPoint.groupSize ? ['Add Group Size'] : []),
        ...(dataPoint.sessionLength ? ['Add Session Length'] : []),
        ...(dataPoint.cptCodes ? ['Add CPT Code'] : []),
        ...(dataPoint.additionalStaff !== undefined && dataPoint.additionalStaff !== '' 
          ? ['Add Staff Present Information'] : []),
        // Add custom section options based on customData content
        ...(() => {
          const customSectionOptions = [];
          const convertedCustomSection = convertCustomDataToCustomSection(
            dataPoint.customData,
            allGoalData
          );
          convertedCustomSection.forEach((section) => {
            // Push the unique docId so duplicates titles don't clash
            customSectionOptions.push(section.docId);
          });
          return customSectionOptions;
        })(),
      ],
      abcData:
        typeof dataPoint.abc_data === 'object'
          ? dataPoint.abc_data
          : {
              antecedent: '',
              behavior: '',
              consequence: '',
              setting: '',
              notes: '',
            },
      phaseChange: dataPoint.phase_change || '',
      precedingBehavior: dataPoint.preceding_behavior || '',
      eventLine: dataPoint.event || '',
      timeOfCollection: dataPoint.timeOfCollection || '',
      timeOfCollectionSpecific: dataPoint.timeOfCollectionSpecific || '',
      promptingLevel: dataPoint.promptingLevel || '',
      physicalAssistanceLevel: dataPoint.physicalAssistanceLevel || '',
      notes: dataPoint.notes || '',
      sessionSummary: dataPoint.sessionSummary || '',
      groupSize: dataPoint.groupSize || '',
      sessionLength: dataPoint.sessionLength || '',
      cptCodes: dataPoint.cptCodes || '',
      additionalStaff:
        typeof dataPoint.additionalStaff === 'boolean'
          ? dataPoint.additionalStaff
          : dataPoint.additionalStaff === ''
          ? ''
          : false,
      numberOfStaffs:
        typeof dataPoint.numberOfStaffs === 'number'
          ? dataPoint.numberOfStaffs
          : 0,
      prompting: promptingData,
      criteria: criteriaData,
      currentGoal: computedCurrentGoal,
      taskAnalysis: dataPoint.taskAnalysis || {},
      interventions: dataPoint.interventions || [],
      customSection: convertCustomDataToCustomSection(
        dataPoint.customData,
        allGoalData
      ),
    };
  });
  return result;
};

export const trackDataPointChanges = (existingEntry, formData) => {
  const changes = [];

  const existingDataPoints =
    existingEntry?.dataPoints?.info?.data_points ||
    existingEntry?.info?.data_points ||
    [];
  const newDataPoints = formData?.data_points || [];

  const existingDataPointsMap = new Map();
  const newDataPointsMap = new Map();

  existingDataPoints.forEach((dp) => {
    if (dp?.id) {
      existingDataPointsMap.set(dp.id, dp);
    }
  });

  newDataPoints.forEach((dp) => {
    if (dp?.id) {
      newDataPointsMap.set(dp.id, dp);
    }
  });

  const matchedIds = new Set();

  newDataPoints.forEach((newDP, index) => {
    if (existingDataPointsMap.has(newDP?.id)) {
      const existingDP = existingDataPointsMap.get(newDP.id);
      matchedIds.add(newDP.id);

      const dataPointPrefix = `Data Point #${index + 1}`;
      const flatExistingDP = flattenObjectWithPath(existingDP || {});
      const flatNewDP = flattenObjectWithPath(newDP || {});

      const fieldMapping = {
        trials_range: 'trials_value',
        trials_value: 'trials_range',
      };

      const allFields = new Set([
        ...Object.keys(flatExistingDP),
        ...Object.keys(flatNewDP),
      ]);

      allFields.forEach((fieldPath) => {
        if (fieldPath === 'id' || fieldPath === 'currentTime') return;

        let existingValue = flatExistingDP[fieldPath];
        let newValue = flatNewDP[fieldPath];

        const baseField = fieldPath.split(' > ')[0];
        if (existingValue === undefined && fieldMapping[baseField]) {
          const mappedPath = fieldPath.replace(
            baseField,
            fieldMapping[baseField]
          );
          existingValue = flatExistingDP[mappedPath];
        }

        if (newValue === undefined && fieldMapping[baseField]) {
          const mappedPath = fieldPath.replace(
            baseField,
            fieldMapping[baseField]
          );
          newValue = flatNewDP[mappedPath];
        }

        if (!areValuesSemanticallySame(existingValue, newValue)) {
          changes.push({
            type: 'modified',
            fieldName: `${dataPointPrefix} > ${fieldPath}`,
            oldValue: existingValue,
            newValue: newValue,
          });
        }
      });
    }
  });

  newDataPoints.forEach((newDP, index) => {
    if (!existingDataPointsMap.has(newDP?.id)) {
      changes.push({
        type: 'added',
        fieldName: 'New data point added',
        oldValue: '',
        newValue: '',
      });
    }
  });

  existingDataPoints.forEach((existingDP) => {
    if (!newDataPointsMap.has(existingDP?.id)) {
      changes.push({
        type: 'removed',
        fieldName: 'Data point deleted',
        oldValue: '',
        newValue: '',
      });
    }
  });

  return changes;
};
